// Sprint 6.7 — composer trigger detection.
//
// Given the current textarea value + caret position, decide whether the
// `@` or `/` dropdown should be open. A trigger is valid only when the
// `@` / `/` is at start-of-line OR preceded by whitespace — matches Cursor
// and Claude.ai behavior so email addresses and URLs don't open the dropdown
// mid-word.
//
// Returned range `[start, end]` is the [inclusive, exclusive) slice covering
// the trigger char + any chars already typed as the query. Replacing this
// slice on select inserts `/<name> ` cleanly without double-triggering.

export interface DetectedTrigger {
  char: '@' | '/';
  query: string;
  /** Inclusive start index of the trigger char. */
  start: number;
  /** Exclusive end index — equal to caret when the trigger is active. */
  end: number;
}

const TRIGGER_CHARS = new Set(['@', '/']);
// Query-terminating characters — anything here ends the token and abandons
// the dropdown. Include newline + tab explicitly (some browsers report them
// outside the \s class depending on locale).
const TERMINATORS = new Set([' ', '\n', '\t', '\r']);

export function detectTrigger(
  text: string,
  caret: number,
): DetectedTrigger | null {
  if (caret < 1 || caret > text.length) return null;

  // Walk back from caret - 1 collecting characters until we hit whitespace or
  // the start of the buffer. Along the way, remember the LEFTMOST position
  // that looks like a valid trigger (start-of-buffer or preceded by
  // whitespace). This is key for paths like `@docs/spec.md` — the inner `/`
  // is NOT a trigger (it's preceded by a letter), but the outer `@` is.
  let triggerIdx = -1;
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i]!;
    if (TERMINATORS.has(ch)) break;
    if (TRIGGER_CHARS.has(ch)) {
      // Valid only at start-of-buffer or after whitespace. Mid-word `@`
      // (email) and mid-path `/` (URL, unix path) fail this check.
      const prev = i === 0 ? null : text[i - 1]!;
      if (prev === null || TERMINATORS.has(prev)) {
        triggerIdx = i;
        break; // outermost valid trigger — stop walking
      }
    }
    i--;
  }

  if (triggerIdx === -1) return null;

  const char = text[triggerIdx] as '@' | '/';
  const query = text.slice(triggerIdx + 1, caret);
  return { char, query, start: triggerIdx, end: caret };
}

// Build the replacement string + new caret position for an accepted item.
// We append a single trailing space so the user can continue typing without
// landing inside the next trigger.
export function applyTrigger(
  text: string,
  trigger: DetectedTrigger,
  insertText: string,
): { text: string; caret: number } {
  const before = text.slice(0, trigger.start);
  const after = text.slice(trigger.end);
  const inserted = `${insertText} `;
  const newText = `${before}${inserted}${after}`;
  const newCaret = before.length + inserted.length;
  return { text: newText, caret: newCaret };
}
